/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */
import React, {
  FC,
  forwardRef,
  ReactNode,
  TextareaHTMLAttributes,
  useState,
  useEffect,
} from 'react';
import { FieldValues, UseFormRegister } from 'react-hook-form';
import { Warning } from '..';
import ErrorMessage from '../error-message/error-message';

export interface TextAreaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  children?: ReactNode;
  height?: number;
  label?: string;
  limit?: number;
  register?: UseFormRegister<FieldValues>;
  errors?: any;
  errorMessage?: string;
  isValid?: boolean;
  watch?: (name: string) => string;
}

export const TextArea: FC<TextAreaProps> = forwardRef<
  HTMLTextAreaElement,
  TextAreaProps
>(
  (
    {
      children,
      className,
      cols = 5,
      height = 100,
      label,
      limit = 50,
      name,
      onChange,
      rows = 3,
      register,
      errors,
      errorMessage,
      isValid = true,
      onBlur,
      value,
      watch,
      ...rest
    },
    _ref
  ) => {
    const [charsCount, setCharsCount] = useState<number>(
      value && typeof value !== 'number' ? value.length : 0
    );

    useEffect(() => {
      if (register && name && watch && watch(name)?.length) {
        setCharsCount(watch(name).length);
      }
    }, [watch && watch(name || '')]);

    return (
      <div>
        <label
          htmlFor={name}
          className={`text-form-text text-thryv-steel font-open-sans font-normal capitalize ${
            ((errors && name && errors[name] && errors[name].message) ||
              !isValid) &&
            'text-utility-error-danger'
          }`}
        >
          <span className="pb-[7px] block">{label}</span>
          <textarea
            cols={cols}
            className={`peer block appearance-none w-full px-[13px] pt-[9px] pb-[11px] text-p-base text-thryv-black-500 font-open-sans 
          border border-thryv-gray-light-500 rounded resize-none ring-0 focus:border-thryv-black-500 focus:ring-1 focus:ring-thryv-black-500 focus:outline-none ${
            ((errors && name && errors[name] && errors[name].message) ||
              !isValid) &&
            'border border-utility-error-danger focus:border-utility-error-danger focus:border focus:ring-1 focus:ring-utility-error-danger peer-focus:border-2 peer-focus:border-utility-error-danger peer-focus:ring-utility-error-danger peer-focus:ring-1'
          } transition-all duration-200 ease-in-out ${className || ''} `}
            maxLength={limit}
            // Make sure register is set before onChange to populate the input's ref
            {...(register && {
              ...register(`${name}`),
            })}
            name={name}
            onBlur={onBlur}
            onChange={(e) => {
              if (!onChange && register) {
                const { onChange } = register(`${name}`);
                setCharsCount(e.target.value.length);
                onChange({ target: e.target, type: e.type });
              } else if (onChange && !register) {
                setCharsCount(e.target.value.length);
                onChange(e);
              } else {
                setCharsCount(e.target.value.length);
              }
            }}
            rows={rows}
            value={value}
            style={{ height: `${height}` }}
            {...rest}
          >
            {children}
          </textarea>
          <div className="w-full mt-1">
            {errors && name && errors[name] && errors[name].message && (
              <ErrorMessage
                errorMessage={
                  <>
                    <p>{errors[name].message}</p>
                    <Warning />
                  </>
                }
                styles="w-full flex justify-between items-center"
              />
            )}
            {!isValid && (
              <ErrorMessage
                errorMessage={
                  <>
                    <p>{errorMessage}</p>
                    <Warning />
                  </>
                }
                styles="w-full flex justify-between items-center"
              />
            )}
            {errors && errorMessage && (
              <ErrorMessage
                errorMessage={
                  <>
                    <p>{errorMessage}</p>
                    <Warning />
                  </>
                }
                styles="w-full flex justify-between items-center"
              />
            )}

            {!errors || (errors && name && !errors[name]) ? (
              <span
                className={`w-full flex items-end justify-end text-label-text ${
                  charsCount > limit
                    ? 'text-utility-error-danger'
                    : 'text-thryv-steel'
                } font-open-sans font-normal text-right`}
              >
                {isValid && (
                  <p
                    aria-label="Character Count"
                    aria-describedby={`You have ${
                      limit - charsCount
                    } out of ${limit} characters remaining`}
                  >
                    {limit - charsCount}/{limit}{' '}
                  </p>
                )}
              </span>
            ) : null}
          </div>
        </label>
      </div>
    );
  }
);
